import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import MainLayout from "@/components/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Tags, Mail } from "lucide-react";

const DEFAULT_TEMPLATE = {
  email_subject: "Latest Result Banner",
  email_greeting: "Hi {recipient_name},",
  email_body: "We have just completed the latest lottery draw. Here\u2019s your updated banner \u2014 share it on your website or social media to build awareness with new supporters and keep your current supporters engaged.",
  email_signoff: "{brand_name}",
};

const VARIABLES = [
  { tag: "{recipient_name}", label: "Recipient Name" },
  { tag: "{brand_name}", label: "Brand Name" },
  { tag: "{banner_name}", label: "Banner Name" },
  { tag: "{lottery_name}", label: "Lottery Name" },
];

function VariableTags({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {VARIABLES.map((v) => (
        <button
          key={v.tag}
          type="button"
          onClick={() => onInsert(v.tag)}
          className="px-2 py-0.5 rounded text-[11px] bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 transition-colors border border-[#10B981]/20"
          title={`Insert ${v.tag}`}
        >
          {v.tag}
        </button>
      ))}
    </div>
  );
}

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [form, setForm] = useState({ name: "", sender_email: "", ...DEFAULT_TEMPLATE });

  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const openCreateDialog = () => {
    setEditingBrand(null);
    setForm({ name: "", sender_email: "", ...DEFAULT_TEMPLATE });
    setShowDialog(true);
  };

  const openEditDialog = (brand) => {
    setEditingBrand(brand);
    setForm({
      name: brand.name,
      sender_email: brand.sender_email || "",
      email_subject: brand.email_subject || DEFAULT_TEMPLATE.email_subject,
      email_greeting: brand.email_greeting || DEFAULT_TEMPLATE.email_greeting,
      email_body: brand.email_body || DEFAULT_TEMPLATE.email_body,
      email_signoff: brand.email_signoff || DEFAULT_TEMPLATE.email_signoff,
    });
    setShowDialog(true);
  };

  const insertVariable = (field, tag) => {
    setForm((prev) => ({ ...prev, [field]: prev[field] + tag }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Brand name is required");
      return;
    }

    try {
      if (editingBrand) {
        await axios.put(`${API}/brands/${editingBrand.id}`, form);
        toast.success("Brand updated");
      } else {
        await axios.post(`${API}/brands`, form);
        toast.success("Brand created");
      }
      setShowDialog(false);
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save brand");
    }
  };

  const handleDelete = async (brand) => {
    if (!confirm(`Delete "${brand.name}"? Banners using this brand will need to be reassigned.`)) return;
    try {
      await axios.delete(`${API}/brands/${brand.id}`);
      toast.success("Brand deleted");
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete brand");
    }
  };

  return (
    <MainLayout title="Brands" subtitle="Manage your lottery brands and email sender identities">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3">
            <span className="text-sm text-gray-400">Total brands: </span>
            <span className="text-sm font-medium text-white" data-testid="total-brands-count">{brands.length}</span>
          </div>
          <Button onClick={openCreateDialog} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="add-brand-btn">
            <Plus className="w-4 h-4 mr-2" />Add Brand
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#10B981]"></div>
          </div>
        ) : brands.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
            <Tags className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No brands yet</h3>
            <p className="text-gray-400 mb-4">Create your first brand to start organising banners</p>
            <Button onClick={openCreateDialog} className="bg-[#10B981] hover:bg-[#059669] text-white">
              <Plus className="w-4 h-4 mr-2" />Add Brand
            </Button>
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-gray-400">Brand Name</TableHead>
                  <TableHead className="text-gray-400">Sender Email</TableHead>
                  <TableHead className="text-gray-400">Email Subject</TableHead>
                  <TableHead className="text-gray-400">Created</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brands.map((brand) => (
                  <TableRow key={brand.id} className="border-white/10 hover:bg-white/5" data-testid={`brand-row-${brand.id}`}>
                    <TableCell className="text-white font-medium">
                      <div className="flex items-center gap-2">
                        <Tags className="w-4 h-4 text-[#10B981]" />
                        {brand.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-300">
                      {brand.sender_email ? (
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-gray-500" />
                          {brand.sender_email}
                        </div>
                      ) : (
                        <span className="text-gray-500">Not set</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">{brand.email_subject || DEFAULT_TEMPLATE.email_subject}</TableCell>
                    <TableCell className="text-gray-400">
                      {brand.created_at ? new Date(brand.created_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(brand)} className="text-gray-400 hover:text-white" data-testid={`edit-brand-${brand.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(brand)} className="text-gray-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-brand-${brand.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Create/Edit Brand Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-[#1a1a24] border-white/10 text-white sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBrand ? "Edit Brand" : "Create Brand"}</DialogTitle>
              <DialogDescription className="text-gray-400">
                {editingBrand ? `Update ${editingBrand.name}` : "Add a new lottery brand"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Brand info */}
              <div className="space-y-2">
                <Label htmlFor="brand-name">Brand Name</Label>
                <Input id="brand-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Raise-a-Lotto" className="bg-white/5 border-white/10 text-white" required data-testid="brand-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sender-email">Sender Email</Label>
                <Input id="sender-email" type="email" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="e.g., results@raise-a-lotto.com" className="bg-white/5 border-white/10 text-white" data-testid="brand-email-input" />
                <p className="text-xs text-gray-500">Domain must be verified in Resend for emails to deliver.</p>
              </div>

              {/* Email Template */}
              <div className="border-t border-white/10 pt-4">
                <h3 className="text-sm font-medium text-white mb-1">Email Template</h3>
                <p className="text-xs text-gray-500 mb-3">Customise the email sent to recipients. Click a variable to insert it.</p>

                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-400">Subject Line</Label>
                    <Input value={form.email_subject} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm" data-testid="email-subject-input" />
                    <VariableTags onInsert={(tag) => insertVariable("email_subject", tag)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-400">Greeting</Label>
                    <Input value={form.email_greeting} onChange={(e) => setForm({ ...form, email_greeting: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm" data-testid="email-greeting-input" />
                    <VariableTags onInsert={(tag) => insertVariable("email_greeting", tag)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-400">Body Text</Label>
                    <textarea value={form.email_body} onChange={(e) => setForm({ ...form, email_body: e.target.value })} rows={4} className="w-full rounded-lg bg-white/5 border border-white/10 text-white text-sm px-3 py-2 focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981] outline-none resize-y" data-testid="email-body-input" />
                    <VariableTags onInsert={(tag) => insertVariable("email_body", tag)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-400">Sign-off</Label>
                    <Input value={form.email_signoff} onChange={(e) => setForm({ ...form, email_signoff: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm" data-testid="email-signoff-input" />
                    <VariableTags onInsert={(tag) => insertVariable("email_signoff", tag)} />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="border-white/10 text-white hover:bg-white/10">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="save-brand-btn">
                  {editingBrand ? "Save Changes" : "Create Brand"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
